import React, { ChangeEventHandler } from 'react';
import {
  Box,
  CogIcon,
  defaultColors,
  Dropdown,
  DropdownItem,
  ExitIcon,
  IconSize,
  LanguageIcon,
  Select,
  Flex,
} from '@lifechurch/react-ion';
import { Avatar, AvatarSizeType, post } from '@churchonline/core';
import { CurrentSubscriberFieldsFragment } from '@generated/types';
import { resolveAvatarSrc } from '@utils/imgproxy';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

interface ProfileProps {
  currentSubscriber?: CurrentSubscriberFieldsFragment;
}

const LanguageFlex = styled(Flex)`
  &:hover select {
    color: ${props => props.theme.colors.action.hover};
    background-color: ${props => props.theme.colors.action.light};
  }
`;

const CircularAvatarBox = styled(Box)`
  img {
    height: 100%;
    object-fit: cover;
    width: 100%;
  }
`;

const Profile: React.FC<ProfileProps> = ({ currentSubscriber }) => {
  const router = useRouter();
  const avatar: string | undefined = currentSubscriber?.avatar ?? undefined;
  // 40px to match AvatarSizeType.MEDIUM below — avoids downloading the
  // full-resolution original for a small avatar circle.
  const resolvedAvatar = resolveAvatarSrc(avatar, 40);
  const nickname: string = currentSubscriber?.nickname ?? '';
  const id = currentSubscriber?.id;
  const { t } = useTranslation('common');

  const logout = async (event: { preventDefault: () => void }): Promise<void> => {
    // Prevents certain browser from starting their navigation change before the API can be
    event.preventDefault();

    try {
      await post('/auth/logout');
    } catch (err) {
      if (err) throw new Error();
    }
    location.replace('/');
  };

  const selectLanguage: ChangeEventHandler<HTMLInputElement | HTMLSelectElement> = ({
    target: { value },
  }) => {
    router.push('/', '/', { locale: value });
  };

  return (
    <Dropdown
      popperContent={
        <Box>
          <DropdownItem data-testid='logout' href={``} onClick={logout}>
            <ExitIcon marginInlineEnd={2} size={IconSize.MD} />
            {t('logout')}
          </DropdownItem>
          <DropdownItem alignItems='center' as={LanguageFlex}>
            <LanguageIcon color={defaultColors.gray100} marginInlineEnd={2} size={IconSize.MD} />
            <Select
              data-hj-whitelist
              name='language'
              onChange={selectLanguage}
              style={{ border: 'none', paddingInlineStart: 0 }}
              value={router.locale}
            >
              <option value='af-ZA'>Afrikaans</option>
              <option value='ar-SA'>العربية</option>
              <option value='be-BY'>беларуская</option>
              <option value='bg-BG'>български</option>
              <option value='ca-ES'>català</option>
              <option value='cs-CZ'>čeština</option>
              <option value='cy-GB'>Cymraeg</option>
              <option value='da-DK'>Dansk</option>
              <option value='de-DE'>Deutsch</option>
              <option value='es-ES'>Español (Spain)</option>
              <option value='es-LA'>Español (Latin America)</option>
              <option value='en-GB'>British English</option>
              <option value='en-US'>English</option>
              <option value='fil-PH'>filipino</option>
              <option value='fi-FI'>Suomi</option>
              <option value='fr-FR'>Français</option>
              <option value='hi-IN'>हिन्दी</option>
              <option value='hr-HR'>hrvatski</option>
              <option value='id-ID'>bahasa Indonesia</option>
              <option value='it-IT'>Italiano</option>
              <option value='ja-JP'>日本語</option>
              <option value='ko-KR'>한국어</option>
              <option value='lv-LV'>latviešu valoda</option>
              <option value='mn-MN'>Монгол</option>
              <option value='ms-MY'>Bahasa Melayu</option>
              <option value='nl-NL'>Nederlands</option>
              <option value='no-NO'>Norsk</option>
              <option value='pl-PL'>Polski</option>
              <option value='pt-BR'>Português(Brasil)</option>
              <option value='pt-PT'>Português(Portugal)</option>
              <option value='ru-RU'>Русский</option>
              <option value='ro-RO'>Română</option>
              <option value='sk-SK'>Slovenčina</option>
              <option value='sv-SE'>Svenska</option>
              <option value='ta-IN'>தமிழ்</option>
              <option value='tr-TR'>Türkçe</option>
              <option value='uk-UA'>Українська</option>
              <option value='vi-VN'>Tiếng Việt</option>
              <option value='zh-CN'>简体中文</option>
              <option value='zh-HK'>廣東話 / 广东话</option>
              <option value='zh-TW'>繁體中文</option>
              <option value='zu-ZA'>isiZulu</option>
            </Select>
          </DropdownItem>
          <Link href={`/user/${currentSubscriber?.id}`}>
            <DropdownItem data-testid='edit'>
              <CogIcon color={defaultColors.gray100} marginInlineEnd={2} size={IconSize.MD} />
              {t('edit_profile')}{' '}
            </DropdownItem>
          </Link>
        </Box>
      }
      referenceContent={
        <CircularAvatarBox
          borderRadius='50%'
          height={AvatarSizeType.MEDIUM}
          overflow='hidden'
          width={AvatarSizeType.MEDIUM}
        >
          <Avatar
            isActionable
            size={AvatarSizeType.MEDIUM}
            subscriber={{ avatar: resolvedAvatar, nickname, id }}
          />
        </CircularAvatarBox>
      }
    />
  );
};

export default Profile;
